import { useState } from 'react';
import { Button, Spinner } from '@chakra-ui/react';
import { FileSpreadsheet } from 'lucide-react';
import { exportEventVolunteers } from '@/utils/exportEventVolunteers';

interface ExportVolunteersButtonProps {
  eventId: string | null;
  eventName: string | null | undefined;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

/** Downloads the selected event's accepted volunteers as an Excel file. Org view only. */
export function ExportVolunteersButton({
  eventId,
  eventName,
  onToast,
}: ExportVolunteersButtonProps) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!eventId || exporting) return;
    setExporting(true);
    try {
      const result = await exportEventVolunteers(eventId, eventName);
      if (result.ok) onToast('Export ready', 'success');
      else onToast('No volunteers to export', 'info');
    } catch {
      onToast("Couldn't export volunteers", 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      flexShrink={0}
      bg="volyx.bg"
      border="1.5px solid"
      borderColor="volyx.primary"
      color="volyx.primary"
      borderRadius="xl"
      w="48px"
      h="48px"
      p="0"
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow="0 8px 20px rgba(0,0,0,0.08)"
      cursor="pointer"
      aria-label="Export volunteers to Excel"
      title="Export volunteers to Excel"
      disabled={!eventId || exporting}
      _hover={{
        bg: 'volyx.lightGray',
        boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
      }}
      onClick={handleExport}
    >
      {exporting ? <Spinner size="sm" /> : <FileSpreadsheet size={20} />}
    </Button>
  );
}
